import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useAssignSeat } from "@components/table/members/assign-seats/use-assign-seat";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { updateMemberRoleInGroupWithoutErrorToast } from "@store/groups/groups-slice-thunk";
import { useAppDispatch } from "@store/store-helper";

/** Where the seat is assigned from, as tracked */
const SEAT_LOCATION = "change-management-dialog";

/** What has to happen before the new project manager can manage the project */

/** Returns the member as holding a Sphere XG seat, which is the `activeSeat` permission */
function withXgSeat(member) {
  return {
    ...member,
    permissions: [...(member.permissions ?? []).filter(({
      name
    }) => name !== "activeSeat"),
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Used in backend
    {
      name: "activeSeat",
      allowed: true
    }]
  };
}

/**
 * Callbacks about the steps that already worked, so that trying again after a later step failed
 * does not repeat them. Neither the workspace members in the store nor the group members, which are
 * fetched again only afterwards, tell about them right away.
 */

/** Returns how to prepare the new project manager of a project, see prepareManager */
export function usePrepareManager({
  onManagerUpdated,
  onGroupManagerAdded
}) {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    assignSeat
  } = useAssignSeat();
  async function prepareManager({
    selectedMember,
    groupId,
    groupMembershipChange,
    groupRole,
    shouldAssignSeat
  }) {
    // A member invited by email only exists once invited, so the seat can only be given afterwards
    if (typeof selectedMember === "string") {
      const invitedMember = await inviteByEmail(selectedMember, groupId, groupRole);
      if (!invitedMember) {
        return undefined;
      }
      onManagerUpdated(invitedMember);
      onGroupManagerAdded(invitedMember.identity, groupId);
      if (shouldAssignSeat && !(await assignXgSeat(invitedMember))) {
        return undefined;
      }
      return invitedMember.identity;
    }
    if (shouldAssignSeat && !(await assignXgSeat(selectedMember))) {
      return undefined;
    }
    if (groupMembershipChange === "join" && !(await addToGroup(selectedMember.identity, groupId, groupRole))) {
      return undefined;
    }
    if (groupMembershipChange === "changeRole" && !(await changeRoleInGroup(selectedMember.identity, groupId, groupRole))) {
      return undefined;
    }
    if (groupMembershipChange !== "none") {
      onGroupManagerAdded(selectedMember.identity, groupId);
    }
    return selectedMember.identity;
  }

  /**
   * Gives the new project manager a Sphere XG seat
   *
   * @returns whether that worked, telling the user why not otherwise
   */
  async function assignXgSeat(member) {
    if (!(await assignSeat(member, "xgSeat", SEAT_LOCATION))) {
      return false;
    }
    onManagerUpdated(withXgSeat(member));
    return true;
  }

  /**
   * Invites somebody to the workspace and the group by email
   *
   * @returns the new member as the group lists them, or undefined when that failed, telling the
   * user why
   */
  async function inviteByEmail(email, groupId_0, role) {
    const groupMembers = await addToGroup(email, groupId_0, role);
    const lowerEmail = email.toLowerCase();
    const member_0 = groupMembers?.find(groupMember => groupMember.email?.toLowerCase() === lowerEmail);
    if (groupMembers && !member_0) {
      handleErrorWithToast({
        id: `inviteProjectManager-${groupId_0}-${Date.now().toString()}`,
        title: "Cannot find the new project manager in the group",
        error: new Error("The invited member is not listed in the group")
      });
    }
    return member_0;
  }

  /**
   * Adds a member to the group, or invites them to the workspace and the group by email
   *
   * @returns the members of the group then, or undefined when that failed, telling the user why
   */
  async function addToGroup(identity, groupId_1, role_0) {
    try {
      const response = await faroCoreApiClient.groups.addMembersToGroup({
        companyId,
        groupId: groupId_1,
        payload: {
          assignments: [{
            identities: [identity],
            role: role_0
          }]
        }
      });

      // The failure is passed on as it is, since its message can name the member
      const failure = response.errorData?.details.failedInvitations[0];
      if (failure) {
        showAddToGroupError(groupId_1, failure);
        return undefined;
      }
      return response.data.members ?? [];
    } catch (error) {
      showAddToGroupError(groupId_1, error);
      return undefined;
    }
  }

  /** Tells the user that the new project manager could not be added to the group */
  function showAddToGroupError(groupId_2, error_0) {
    handleErrorWithToast({
      id: `addProjectManagerToGroup-${groupId_2}-${Date.now().toString()}`,
      title: "Cannot add the new project manager to the group",
      error: error_0
    });
  }

  /**
   * Changes the role of a member in the group
   *
   * @returns whether that worked, telling the user why not otherwise
   */
  async function changeRoleInGroup(userIdentity, groupId_3, role_1) {
    try {
      await dispatch(updateMemberRoleInGroupWithoutErrorToast({
        companyId,
        groupId: groupId_3,
        userIdentity,
        role: role_1
      })).unwrap();
      return true;
    } catch (error_1) {
      handleErrorWithToast({
        id: `changeProjectManagerGroupRole-${groupId_3}-${Date.now().toString()}`,
        title: "Cannot change the role of the new project manager in the group",
        error: error_1
      });
      return false;
    }
  }
  return {
    prepareManager
  };
}